<script lang="ts">
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { toast } from "svelte-sonner";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import UploadIcon from "@lucide/svelte/icons/upload";
	import XIcon from "@lucide/svelte/icons/x";
	import CheckCircleIcon from "@lucide/svelte/icons/check-circle";
	import LoaderIcon from "@lucide/svelte/icons/loader";
	import { getFileContentType, isImageFile } from "@fergmar/ui/files/file-content-type";
	import { isBrowserImageType } from "@fergmar/ui/files/image-display";
	import { heicPreviewUrl, isHeicFile } from "@fergmar/ui/files/heic-preview";
	import { putToPresignedUrl } from "@fergmar/ui/files/presigned-upload";
	import type { StagedReferralPhoto } from "@fergmar/contracts/public/referrals";
	import { v4 as uuidv4 } from "uuid";

	let {
		referralId,
		accept = "image/*",
		maxFiles = 10,
		maxFileSizeMB = 10,
		successCount = $bindable(0),
		pendingCount = $bindable(0),
		uploadingCount = $bindable(0),
		errorCount = $bindable(0),
		stagedPhotos = $bindable([]),
		onStateChange
	}: {
		referralId: string;
		accept?: string;
		maxFiles?: number;
		maxFileSizeMB?: number;
		successCount?: number;
		pendingCount?: number;
		uploadingCount?: number;
		errorCount?: number;
		stagedPhotos?: StagedReferralPhoto[];
		onStateChange?: (state: {
			photos: StagedReferralPhoto[];
			pendingCount: number;
			uploadingCount: number;
			errorCount: number;
		}) => void;
	} = $props();

	type UploadItem = {
		id: string;
		file: File;
		status: "pending" | "uploading" | "success" | "error";
		progress: number;
		errorMessage?: string;
		previewUrl?: string;
		stagedPhoto?: StagedReferralPhoto;
	};

	let uploadQueue = $state<UploadItem[]>([]);
	const fileInputId = $derived(`referral-files-${referralId}`);

	function selectFiles() {
		document.getElementById(fileInputId)?.click();
	}

	function syncState() {
		successCount = uploadQueue.filter((i) => i.status === "success").length;
		pendingCount = uploadQueue.filter((i) => i.status === "pending").length;
		uploadingCount = uploadQueue.filter((i) => i.status === "uploading").length;
		errorCount = uploadQueue.filter((i) => i.status === "error").length;
		onStateChange?.({ photos: stagedPhotos, pendingCount, uploadingCount, errorCount });
	}

	syncState();

	function handleFileSelect(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files) return;

		const newFiles = Array.from(input.files);
		const maxSizeBytes = maxFileSizeMB * 1024 * 1024;

		for (const file of newFiles) {
			if (uploadQueue.length >= maxFiles) {
				toast.error(`Maximum ${maxFiles} files allowed`);
				break;
			}
			if (file.size > maxSizeBytes) {
				toast.error(`${file.name} exceeds ${maxFileSizeMB}MB limit`);
				continue;
			}
			if (file.size === 0 || (accept.startsWith("image/") && !isImageFile(file))) {
				toast.error(`${file.name} must be a nonempty image`);
				continue;
			}

			const item: UploadItem = {
				id: uuidv4(),
				file,
				status: "pending",
				progress: 0,
				previewUrl: isBrowserImageType(getFileContentType(file))
					? URL.createObjectURL(file)
					: undefined
			};
			uploadQueue = [...uploadQueue, item];
			if (isHeicFile(file)) {
				void heicPreviewUrl(file).then((url) => {
					const queued = uploadQueue.find((queuedItem) => queuedItem.id === item.id);
					if (queued && url) queued.previewUrl = url;
					else if (url) URL.revokeObjectURL(url);
				});
			}
		}

		input.value = "";

		syncState();
		void processUploads();
	}

	function removeItem(id: string) {
		const item = uploadQueue.find((i) => i.id === id);
		if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
		if (item?.stagedPhoto) {
			stagedPhotos = stagedPhotos.filter(
				(photo) => photo.providerKey !== item.stagedPhoto?.providerKey
			);
		}
		uploadQueue = uploadQueue.filter((i) => i.id !== id);
		syncState();
	}

	let uploadsInFlight = false;

	async function processUploads() {
		if (uploadsInFlight) return;
		uploadsInFlight = true;
		try {
			while (true) {
				const pending = uploadQueue.filter((i) => i.status === "pending");
				if (pending.length === 0) break;
				await uploadBatch(pending);
			}
		} finally {
			uploadsInFlight = false;
		}
	}

	async function uploadBatch(pending: UploadItem[]) {
		for (const item of pending) {
			if (
				!uploadQueue.some((queued) => queued.id === item.id && queued.status === "pending")
			) {
				continue;
			}
			item.status = "uploading";
			item.progress = 0;
			syncState();

			try {
				const upload = await trpcBrowser.public.files.getAcquisitionReferralUploadUrl.query(
					{
						referralId,
						fileName: item.file.name,
						contentType: getFileContentType(item.file),
						contentLength: item.file.size
					}
				);
				await putToPresignedUrl(upload, item.file, (progress) => {
					item.progress = progress;
				});

				const stagedPhoto = {
					providerKey: upload.providerKey,
					allocationToken: upload.allocationToken,
					displayName: item.file.name
				} satisfies StagedReferralPhoto;
				stagedPhotos = [...stagedPhotos, stagedPhoto];

				item.status = "success";
				item.progress = 100;
				item.stagedPhoto = stagedPhoto;
				syncState();
			} catch (e) {
				console.error("Photo upload failed", e);
				item.status = "error";
				item.errorMessage = "Upload failed. Please try again.";
				syncState();
			}
		}
	}

	function retryUploads() {
		for (const item of uploadQueue) {
			if (item.status === "error") {
				item.status = "pending";
				item.errorMessage = undefined;
			}
		}
		syncState();
		void processUploads();
	}

	const hasPending = $derived(uploadQueue.some((i) => i.status === "pending"));
	const hasErrors = $derived(uploadQueue.some((i) => i.status === "error"));
	const isUploading = $derived(uploadQueue.some((i) => i.status === "uploading"));
</script>

<div class="space-y-3">
	<input
		id={fileInputId}
		type="file"
		{accept}
		multiple
		class="hidden"
		onchange={handleFileSelect}
	/>

	<Button type="button" variant="outline" size="sm" onclick={selectFiles} disabled={isUploading}>
		<UploadIcon class="size-4" />
		Select Files
	</Button>

	{#if uploadQueue.length > 0}
		<div class="space-y-2">
			{#each uploadQueue as item (item.id)}
				<div class="flex items-center gap-3 rounded border p-2">
					{#if item.previewUrl}
						<img src={item.previewUrl} alt="" class="size-10 rounded object-cover" />
					{/if}
					<div class="min-w-0 flex-1">
						<p class="truncate text-sm">{item.file.name}</p>
						{#if item.status === "uploading"}
							<progress
								class="bg-muted accent-primary mt-1 h-1.5 w-full overflow-hidden rounded-full"
								value={item.progress}
								max="100"
							></progress>
						{/if}
						{#if item.status === "error"}
							<p class="text-destructive text-xs">{item.errorMessage}</p>
						{/if}
					</div>
					<div class="flex-shrink-0">
						{#if item.status === "uploading"}
							<LoaderIcon class="size-4 animate-spin" />
						{:else}
							<button
								type="button"
								onclick={() => removeItem(item.id)}
								class="flex items-center gap-1 p-1"
								aria-label={`Remove ${item.file.name}`}
							>
								{#if item.status === "success"}
									<CheckCircleIcon class="size-4 text-green-600" />
								{/if}
								<XIcon class="size-4" />
							</button>
						{/if}
					</div>
				</div>
			{/each}
		</div>

		{#if hasPending || hasErrors}
			<Button type="button" size="sm" onclick={retryUploads} disabled={isUploading}>
				{isUploading
					? "Uploading..."
					: `${hasErrors ? "Retry" : "Upload"} ${uploadQueue.filter((i) => i.status === "pending" || i.status === "error").length} file(s)`}
			</Button>
		{/if}
	{/if}
</div>
